import React, {useEffect, useRef} from 'react';
import _ from 'lodash';

import {cx} from '../../util';
import {
  InfoBox, DifficultyScale, CompBreakdown, ReflectionsSection, QuestionsSection,
} from '../../components/InterviewComponents';
import {getCountryName} from '../../util/countries';
import {LEVEL_LABELS, RESULT_LABELS, WORK_TYPE_LABELS} from '../../../../lib/utils';

// Reusable placeholder component
const Placeholder = ({className}) => (
  <div className={cx('bg-gray-100 rounded', className)} />
);

// Placeholder for sections (questions/reflections)
const SectionPlaceholder = ({headerWidth = 'w-24', lines = [], className}) => (
  <div className={cx('space-y-4', className)}>
    <Placeholder className={cx('h-6', headerWidth)} />
    <div className="space-y-3">
      {lines.map((width) => (
        <Placeholder key={width} className={cx('h-4', width)} />
      ))}
    </div>
  </div>
);

const CompanyLogoFallback = ({hasContent, company}) => {
  if (hasContent) {
    return (
      <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 font-bold text-lg shadow-sm">
        {(company || '?').charAt(0).toUpperCase()}
      </div>
    );
  }
  return <Placeholder className="w-14 h-14 rounded-xl border border-gray-100" />;
};

const InterviewPreview = ({draft, tags, displayStep}) => {
  const maxStepReached = useRef(displayStep);

  // Hack to conditionally display difficulty if user has reached ReflectionStep
  useEffect(() => {
    if (displayStep > maxStepReached.current) {
      maxStepReached.current = displayStep;
    }
  }, [displayStep]);

  const companyTag = _.find(
    tags?.company ?? [],
    (t) => t.id === draft.company || t.name === draft.company,
  );
  const roleTag = _.find(tags?.role ?? [], (t) => t.id === draft.role);
  const companyName = companyTag?.name || draft.company;
  const roleName = roleTag?.name || draft.jobTitle || draft.role;

  // Compute display values
  const lengthValue = draft.durationValue && draft.durationUnit
    ? `${draft.durationValue} ${draft.durationUnit}`
    : null;
  const roundsValue = draft.stages?.length
    ? `${draft.stages.length} round${draft.stages.length !== 1 ? 's' : ''}`
    : null;
  const lastInterviewValue = draft.askedOnMonth && draft.askedOnYear
    ? `${draft.askedOnMonth} ${draft.askedOnYear}`
    : null;
  const resultValue = draft.result ? RESULT_LABELS[draft.result] : null;

  // Calculate total comp
  const salary = parseInt(draft.salary, 10) || 0;
  const stock = parseInt(draft.stock, 10) || 0;
  const bonus = parseInt(draft.bonus, 10) || 0;
  const totalComp = salary + stock + bonus;
  const showComp = totalComp > 0;

  // Check if preview has any meaningful content
  const showDifficulty = draft.difficulty && maxStepReached.current >= 3;
  const hasCompanyOrRole = draft.company || draft.role;
  const hasQuestions = (draft.stages || []).some(
    (s) => s.questions?.some((q) => q.text?.trim()),
  );
  const hasReflections = draft.overview?.trim()
    || draft.tips?.trim()
    || draft.companyCulture?.trim();
  const hasMetadata = lengthValue || roundsValue || lastInterviewValue
    || resultValue || showDifficulty;

  const isEmpty = !hasCompanyOrRole && !hasMetadata && !hasQuestions && !hasReflections;

  return (
    <div
      className="relative flex flex-col h-full min-h-0 rounded-2xl border border-gray-200 overflow-hidden shadow-sm"
      style={{background: 'linear-gradient(to top, rgba(238, 242, 255, 0.8), rgba(250, 245, 255, 0.4), white)'}}
    >
      {/* Preview badge */}
      <div className="absolute top-4 right-4 z-10">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </span>
      </div>

      <div className="flex-1 flex flex-col gap-6 pt-12 px-8 pb-8 overflow-y-auto scrollbar-hidden">
        <div className="max-w-xl mx-auto w-full">
          {/* Header section */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Company logo */}
            <div className="flex-shrink-0">
              {companyTag?.image_url ? (
                <img
                  src={companyTag.image_url}
                  alt={companyTag.name}
                  className="w-14 h-14 object-contain rounded-xl border border-gray-100 p-2 bg-white shadow-sm"
                />
              ) : (
                <CompanyLogoFallback hasContent={hasCompanyOrRole} company={draft.company} />
              )}
            </div>

            {/* Title and role */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                {roleName ? (
                  <h2 className="font-semibold text-gray-900 text-xl tracking-tight">
                    {roleName}
                  </h2>
                ) : (
                  <Placeholder className="h-6 w-40" />
                )}
                <div className="flex items-center gap-1.5 mt-0.5">
                  {companyName ? (
                    <span className="text-sm text-gray-500">{companyName}</span>
                  ) : (
                    <Placeholder className="h-4 w-24" />
                  )}
                  {companyName && draft.level && (
                    <span className="text-sm text-gray-500">·</span>
                  )}
                  {draft.level && (
                    <span className="text-sm text-gray-500">{LEVEL_LABELS[draft.level] || draft.level}</span>
                  )}
                </div>
              </div>

              {/* Location and work type badges */}
              {(draft.location || draft.workType) && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {draft.location && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {getCountryName(draft.location) || draft.location}
                    </span>
                  )}
                  {draft.workType && (
                    <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                      {WORK_TYPE_LABELS[draft.workType] || draft.workType}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Metadata row 1 */}
          <div className="grid grid-cols-4 gap-3 mb-3">
            {(() => {
              const boxes = [
                resultValue && {label: 'Result', value: resultValue},
                lastInterviewValue && {label: 'Interview date', value: lastInterviewValue},
                lengthValue && {label: 'Timespan', value: lengthValue},
                roundsValue && {label: 'Total rounds', value: roundsValue},
              ].filter(Boolean);
              const placeholderCount = Math.max(0, 3 - boxes.length);
              return (
                <>
                  {boxes.map((box) => (
                    <InfoBox key={box.label} label={box.label} value={box.value} />
                  ))}
                  {['a', 'b', 'c'].slice(0, placeholderCount).map((key) => (
                    <Placeholder key={`placeholder-${key}`} className="rounded-xl h-16" />
                  ))}
                </>
              );
            })()}
          </div>

          {/* Metadata row 2 */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {showDifficulty && <DifficultyScale difficulty={draft.difficulty} />}
            {showComp && (
              <CompBreakdown
                salary={salary}
                stock={stock}
                bonus={bonus}
              />
            )}
            {!showDifficulty && <Placeholder className="rounded-xl h-24" />}
          </div>

          {/* Questions section */}
          {hasQuestions
            ? <QuestionsSection stages={draft.stages} />
            : <SectionPlaceholder headerWidth="w-24" lines={['w-96', 'w-80']} />}

          {/* Reflections section */}
          {hasReflections ? (
            <ReflectionsSection
              overview={draft.overview}
              tips={draft.tips}
              companyCulture={draft.companyCulture}
            />
          ) : (
            <SectionPlaceholder headerWidth="w-28" lines={['w-72', 'w-56']} className="mt-8" />
          )}
        </div>
      </div>

      {/* Privacy notice - only shown when empty */}
      {isEmpty && (
        <div className="absolute bottom-0 left-0 right-0 p-8 text-center max-w-xl mx-auto w-full">
          <p className="text-xs text-gray-400 leading-5 tracking-tight">
            This is a preview of how your interview experience will look to other members.
            {' '}
            Your personal information will always remain anonymous.
          </p>
        </div>
      )}
    </div>
  );
};

export default InterviewPreview;
